// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  type Manufacturer,
  type UnitOfMeasure,
  type PartCatalog,
  type WorkOrderItem,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import type { WithNullable } from 'helpers/helper-types';
import { ManufacturerSelect } from 'hooks/typeCacheHooks/ManufacturerSelect';
import { UnitOfMeasureSelect } from 'hooks/typeCacheHooks/UnitOfMeasureSelect';
import { useManufacturers } from 'hooks/typeCacheHooks/useManufacturers';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { getLocalStorageWrapped } from 'hooks/useLocalStorage';
import { CatalogSelect } from 'modules/Shop/CatalogSetup/CatalogSelect';
import { ExistingCategorySelector } from 'modules/Shop/CatalogSetup/ExistingCategorySelector';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

type Props = {
  opened: boolean;
  onClose: () => void;
  workOrderItem: WorkOrderItem;
  onWorkOrderItemUpdated: (updatedWorkOrderItem: WorkOrderItem) => void;
};

type FormValues = {
  name: string;
  description: string | null;
  partCategory: CategoryPath[number];
  manufacturer: Manufacturer;
  unitOfMeasure: UnitOfMeasure;
};

const AddToCatalogForm = ({ workOrderItem, onWorkOrderItemUpdated }: Props) => {
  const defaultCatalog = getLocalStorageWrapped('SELECTED_CATALOG');
  const [selectedCatalog, setSelectedCatalog] = useState<PartCatalog | null>(
    defaultCatalog?.editable ? defaultCatalog : null,
  );
  const { uoms } = useUnitOfMeasure();
  const { manufacturers } = useManufacturers();

  const form = useForm<WithNullable<FormValues>>({
    initialValues: {
      name: workOrderItem.name,
      description: '',
      manufacturer: manufacturers?.find((m) => m.manufacturerId === workOrderItem.manufacturerId) ?? null,
      unitOfMeasure: uoms?.find((u) => u.unitOfMeasureId === workOrderItem.unitOfMeasureId) ?? null,
      partCategory: null,
    },
    validate: {
      name: (v) => !v,
      manufacturer: (v) => !v,
      unitOfMeasure: (v) => !v,
      partCategory: (c) => !c,
    },
  });

  const { apiCall: importWorkOrderItem } = useEvolvePost(`shop/partCatalog/part/importWorkOrderItem/:workOrderItemId`);

  const onSubmit = async (rawValues: typeof form.values) => {
    // Cast is safe due to form.validate
    const {
      partCategory: { partCategoryId },
      manufacturer: { manufacturerId },
      unitOfMeasure: { unitOfMeasureId },
      ...values
    } = rawValues as FormValues;
    const body = {
      ...values,
      manufacturerId,
      unitOfMeasureId,
      partCategoryId,
    };

    await importWorkOrderItem(body, {
      url: `shop/partCatalog/part/importWorkOrderItem/${workOrderItem.workOrderItemId}`,
    }).then(onWorkOrderItemUpdated);
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <CatalogSelect
        showLabel
        selectProps={{ required: true }}
        onCatalogSelected={setSelectedCatalog}
        selectedCatalog={selectedCatalog}
        onlyEditable
      />
      <ExistingCategorySelector required partCatalog={selectedCatalog} {...form.getInputProps('partCategory')} />
      <TextInput label="Generic Part Name" withAsterisk {...form.getInputProps('name')} />
      <Textarea label="Description" {...form.getInputProps('description')} />
      <ManufacturerSelect required {...form.getInputProps('manufacturer')} />
      <UnitOfMeasureSelect required {...form.getInputProps('unitOfMeasure')} />
    </>
  );
};

export const AddToCatalogModal = (props: Props) => {
  const { opened, onClose } = props;
  return (
    <FormModal
      title="Add item to catalog"
      size="lg"
      opened={opened}
      onClose={onClose}
      type="submit"
      confirmationText="Add"
    >
      <AddToCatalogForm {...props} />
    </FormModal>
  );
};
